import { useEffect } from 'react'
import { useThemeStore } from '@/stores/themeStore'
import { defaultTheme, withRocketThemeDefaults } from '@/types/theme'
import { getShellTintBackground } from '@/utils/shellTint'
import { withLightAccessibleText } from '@/utils/wcagText'

// Aplica as variáveis CSS do tema no document.documentElement
function applyThemeVars(theme: ReturnType<typeof useThemeStore.getState>['theme']) {
  const root = document.documentElement

  // Merge com defaults para garantir que variáveis antigas/persistidas sem novos campos funcionem
  const t = withLightAccessibleText(withRocketThemeDefaults({ ...defaultTheme, ...theme }))

  // Modo dark/light via data-theme
  root.setAttribute('data-theme', t.mode)
  const themeColor = document.querySelector('meta[name="theme-color"]')
  if (themeColor) themeColor.setAttribute('content', t.mode === 'light' ? '#f8f8fa' : '#0d0221')

  // Cores primárias
  root.style.setProperty('--brand-primary', t.brandPrimary)
  root.style.setProperty('--brand-primary-hover', t.brandPrimaryHover)
  root.style.setProperty('--brand-primary-muted', t.brandPrimaryMuted)

  // Cores secundárias
  root.style.setProperty('--brand-secondary', t.brandSecondary)
  root.style.setProperty('--brand-secondary-hover', t.brandSecondaryHover)

  // Tipografia — cores
  root.style.setProperty('--font-color-h1', t.fontColorH1)
  root.style.setProperty('--font-color-h2', t.fontColorH2)
  root.style.setProperty('--font-color-h3', t.fontColorH3)
  root.style.setProperty('--font-color-h4', t.fontColorH4)
  root.style.setProperty('--font-color-h5', t.fontColorH5)
  root.style.setProperty('--font-color-h6', t.fontColorH6)
  root.style.setProperty('--font-color-body', t.fontColorBody)
  root.style.setProperty('--font-color-muted', t.fontColorMuted)
  root.style.setProperty('--font-color-label', t.fontColorLabel)

  // Tipografia — tamanhos
  root.style.setProperty('--font-size-hero', t.fontSizeHero)
  root.style.setProperty('--font-size-h1', t.fontSizeH1)
  root.style.setProperty('--font-size-h2', t.fontSizeH2)
  root.style.setProperty('--font-size-h3', t.fontSizeH3)
  root.style.setProperty('--font-size-h4', t.fontSizeH4)
  root.style.setProperty('--font-size-h5', t.fontSizeH5)
  root.style.setProperty('--font-size-h6', t.fontSizeH6)
  root.style.setProperty('--font-size-body', t.fontSizeBody)
  root.style.setProperty('--font-size-sm', t.fontSizeSm)
  root.style.setProperty('--font-size-xs', t.fontSizeXs)
  root.style.setProperty('--font-size-label', t.fontSizeLabel)

  const isLight = t.mode === 'light'
  const degradePct = t.degradePercent ?? 0
  const degradeClr = t.degradeColor ?? '#ebe5f9'
  const resolvedSidebar =
    isLight && degradePct > 0
      ? getShellTintBackground(degradeClr, degradePct, t.bgSidebar)
      : t.bgSidebar
  const resolvedHeader =
    isLight && degradePct > 0
      ? getShellTintBackground(degradeClr, degradePct, t.bgHeader)
      : t.bgHeader

  // Superfícies — sidebar, header, conteúdo
  root.style.setProperty('--bg-dark', t.bgDark)
  root.style.setProperty('--bg-main', t.bgMain)
  root.style.setProperty('--bg-header', resolvedHeader)
  root.style.setProperty('--text-header', t.textHeader)
  root.style.setProperty('--bg-sidebar', resolvedSidebar)
  root.style.setProperty('--text-sidebar', t.textSidebar)
  root.style.setProperty('--nav-active-color', t.navActiveColor ?? t.brandPrimary)
  root.style.setProperty('--nav-active-muted', t.navActiveMuted ?? t.brandPrimaryMuted)
  root.style.setProperty('--bg-card', t.bgCard)
  root.style.setProperty('--text-primary', t.textPrimary)
  root.style.setProperty('--text-secondary', t.textSecondary)
  root.style.setProperty('--text-muted', t.textMuted)

  root.style.setProperty('--border', isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.10)')
  // Fundo de inputs: no claro usa bgDark (#f0f0f3) para contraste sobre cards brancos
  root.style.setProperty('--glass-bg', isLight ? t.bgDark : 'rgba(255, 255, 255, 0.035)')
  root.style.setProperty(
    '--glass-border',
    isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(233, 30, 99, 0.18)',
  )
  root.style.setProperty('--glass-shadow', isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.3)')
  root.style.setProperty('--select-option-bg', t.bgCard)
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useThemeStore((state) => state.theme)

  // Aplica o tema sempre que mudar
  useEffect(() => {
    applyThemeVars(theme)
  }, [theme])

  // Aplica na montagem inicial
  useEffect(() => {
    applyThemeVars(useThemeStore.getState().theme)
  }, [])

  return <>{children}</>
}
